import type { ReactNode } from "react";
import { Box, Button, Group, Text } from "@mantine/core";
import { metaPairs, spark, statusColor } from "../lib/vf";
import { useWorkspaceStore } from "../stores/workspaceStore";
import css from "./primitives.module.css";

// The vocabulary every screen is built from. Each one is a thin composition of
// Mantine primitives plus a CSS module reading --vf-*; nothing here overrides
// Mantine internals.

/** `acme-ai:~$ vf status` — the command line every screen opens with. */
export function Prompt({
  command,
  comment,
  action,
}: {
  command: string;
  comment?: string;
  action?: ReactNode;
}) {
  const workspaceId = useWorkspaceStore((s) => s.currentId);
  return (
    <div className={css.prompt}>
      <span>
        <span className={css.promptSymbol}>{workspaceId ?? "vf"}:~$</span> {command}
        {comment ? <span className={css.promptComment}> {comment}</span> : null}
      </span>
      {action ? (
        <>
          <span className={css.promptSpacer} />
          {action}
        </>
      ) : null}
    </div>
  );
}

export function Panel({
  title,
  right,
  children,
  accentTitle,
  capsTitle,
}: {
  title?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
  /** Renders the header in the accent colour (the REQUEST/RESPONSE panes). */
  accentTitle?: boolean;
  /** Renders the header as a small uppercase column label (documents list). */
  capsTitle?: boolean;
}) {
  const headClass = accentTitle ? css.panelHeadAcc : capsTitle ? css.panelHeadCaps : css.panelHead;
  return (
    <div className={css.panel}>
      {title != null ? (
        <div className={headClass}>
          <span>{title}</span>
          {right}
        </div>
      ) : null}
      {children}
    </div>
  );
}

/**
 * `● ready` — a status dot plus its label, tinted by status. Deliberately
 * inherits font-size: the mockup renders it at whatever the surrounding row
 * uses (13px in tables, 12.5px in the dashboard lists).
 */
export function StatusDot({ status, label }: { status: string; label?: string }) {
  return (
    <Text component="span" fz="inherit" c={statusColor(status)}>
      ● {label ?? status}
    </Text>
  );
}

/** `[x] enabled` / `[ ] disabled` — the mockup's glyph toggle. */
export function VfToggle({
  checked,
  onChange,
  onLabel = "enabled",
  offLabel = "disabled",
  "aria-label": ariaLabel,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  onLabel?: string;
  offLabel?: string;
  "aria-label"?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      className={`${css.toggle} ${checked ? css.toggleOn : css.toggleOff}`}
      onClick={() => onChange(!checked)}
    >
      {checked ? `[x] ${onLabel}` : `[ ] ${offLabel}`}
    </button>
  );
}

export interface Column {
  key: string;
  label: string;
  /** CSS grid track, e.g. "2.4fr". */
  width: string;
}

/**
 * The mockup's tables are CSS grids with fractional columns and a click target
 * on the whole row. Mantine's Table cannot express `fr` tracks, so this renders
 * the same structure with Mantine Box/Text and a grid CSS module.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  renderCell,
  onRowClick,
  note,
  empty,
  fontSize = "13px",
}: {
  columns: Column[];
  rows: T[];
  rowKey: (row: T) => string;
  renderCell: (row: T, columnKey: string) => ReactNode;
  onRowClick?: (row: T) => void;
  note?: ReactNode;
  empty?: ReactNode;
  /** The mockup uses 13px on wide tables and 12.5px on dense ones. */
  fontSize?: string;
}) {
  const template = columns.map((c) => c.width).join(" ");
  return (
    <>
      <div className={css.table}>
        <div className={css.headRow} style={{ gridTemplateColumns: template }}>
          {columns.map((c) => (
            <span key={c.key}>{c.label}</span>
          ))}
        </div>
        {rows.length === 0
          ? empty
          : rows.map((row) => {
              const cells = columns.map((c) => (
                <span key={c.key} className={css.cellTruncate}>
                  {renderCell(row, c.key)}
                </span>
              ));
              const style = { gridTemplateColumns: template, fontSize };
              return onRowClick ? (
                <Box
                  key={rowKey(row)}
                  component="button"
                  type="button"
                  className={`${css.row} ${css.rowClickable}`}
                  style={style}
                  onClick={() => onRowClick(row)}
                >
                  {cells}
                </Box>
              ) : (
                <div key={rowKey(row)} className={css.row} style={style}>
                  {cells}
                </div>
              );
            })}
      </div>
      {note ? <div className={css.tableNote}>{note}</div> : null}
    </>
  );
}

/** Full-width dashed empty state with a call to action. */
export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className={css.emptyDashed}>
      <div className={css.emptyTitle}>{title}</div>
      <div className={css.emptyBody}>{body}</div>
      {action}
    </div>
  );
}

/** Empty state that sits inside a table body. */
export function EmptyRow({
  title,
  body,
  action,
}: {
  title: string;
  body: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={css.emptyInline}>
      <div className={action ? css.emptyTitle : css.emptyTitleSm}>{title}</div>
      <div className={css.emptyBodyDim}>{body}</div>
      {action ? <Box mt="md">{action}</Box> : null}
    </div>
  );
}

export function MetaChips({ metadata }: { metadata: Record<string, unknown> }) {
  const pairs = metaPairs(metadata);
  if (pairs.length === 0) return null;
  return (
    <Group gap={8} wrap="wrap">
      {pairs.map((p) => (
        <span key={p.k} className={css.metaChip}>
          {p.k}=<span className={css.metaChipValue}>{p.v}</span>
        </span>
      ))}
    </Group>
  );
}

/** Key/value block used by every detail slide-over. */
export function KvBox({ rows }: { rows: { k: string; v: ReactNode }[] }) {
  return (
    <div className={css.kvBox}>
      {rows.map((r) => (
        <div key={r.k} className={css.kvRow}>
          <span className={css.kvKey}>{r.k}</span>
          <span className={css.kvValue}>{r.v}</span>
        </div>
      ))}
    </div>
  );
}

export function JsonPane({ value, dim, maxHeight }: { value: string; dim?: boolean; maxHeight?: number }) {
  return (
    <pre
      className={`${css.jsonPane} ${dim ? css.jsonPaneDim : ""} vfsc`}
      style={maxHeight ? { maxHeight } : undefined}
    >
      {value}
    </pre>
  );
}

export function Sparkline({ values, small }: { values: number[]; small?: boolean }) {
  return (
    <pre className={`${css.sparkline} ${small ? css.sparklineSm : ""}`}>
      {values.length ? spark(values) : "▁".repeat(24)}
    </pre>
  );
}

/** The blinking block cursor used on running states. */
export function Cursor() {
  return <span className={css.blink}>_</span>;
}

/** Score bar under a query hit. */
export function ScoreBar({ score }: { score: number }) {
  return (
    <div className={css.scoreTrack}>
      <div className={css.scoreFill} style={{ width: `${Math.round(score * 100)}%` }} />
    </div>
  );
}

export function Screen({ children }: { children: ReactNode }) {
  return <div className={css.fadeIn}>{children}</div>;
}

/**
 * Surfaces a failed load. Without this a 401/500 is indistinguishable from an
 * empty namespace — the screen just renders its "nothing here yet" state.
 */
export function LoadError({ message }: { message: string | null | undefined }) {
  if (!message) return null;
  return (
    <div className={css.loadError} role="alert">
      ✗ {message}
    </div>
  );
}

/** Small accent-on-panel button — the mockup's secondary action. */
export function GhostButton({
  children,
  onClick,
  ...rest
}: {
  children: ReactNode;
  onClick?: () => void;
} & Record<string, unknown>) {
  return (
    <Button
      variant="light"
      size="compact-sm"
      fz="sm"
      onClick={onClick}
      styles={{
        root: {
          background: "var(--vf-accbg)",
          border: "1px solid var(--vf-border)",
          color: "var(--vf-acc)",
          padding: "6px 13px",
          height: "auto",
        },
      }}
      {...rest}
    >
      {children}
    </Button>
  );
}
